import { supabase } from '@/supabase.config';
import type {
  Receipt, ReceiptCategory, ReceiptConcept, ReceiptFilters,
  IssueReceiptInput, PublicReceipt, ReceiptListItem,
} from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const RECEIPT_LIST_SELECT = `
  id, year, number, status, payer_name, payer_ci, issued_at, total, currency,
  void_reason, source_type, source_id,
  category:receipt_categories!category_id(id, name, default_print_format)
`;

const RECEIPT_SELECT = `
  *,
  category:receipt_categories!category_id(id, code, name, default_print_format),
  lines:receipt_lines(id, line_no, concept_id, description, quantity, unit_price, subtotal,
    concept:receipt_concepts!concept_id(id, code, name)),
  payments:receipt_payments(id, method, amount, bank_account_id, check_number, voucher_photo_url)
`;

export async function getReceiptCategories(): Promise<ReceiptCategory[]> {
  const { data, error } = await db
    .from('receipt_categories')
    .select('*')
    .order('name');
  if (error) throw error;
  return (data ?? []) as ReceiptCategory[];
}

export async function getActiveReceiptCategories(): Promise<ReceiptCategory[]> {
  const { data, error } = await db
    .from('receipt_categories')
    .select('*')
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return (data ?? []) as ReceiptCategory[];
}

export async function getReceiptConcepts(): Promise<ReceiptConcept[]> {
  const { data, error } = await db
    .from('receipt_concepts')
    .select('*')
    .eq('is_active', true)
    .order('name');
  if (error) throw error;
  return (data ?? []) as ReceiptConcept[];
}

export async function getReceipts(filters: ReceiptFilters): Promise<ReceiptListItem[]> {
  let query = db
    .from('receipts')
    .select(RECEIPT_LIST_SELECT)
    .eq('year', filters.year)
    .order('issued_at', { ascending: false });

  if (filters.categoryId) query = query.eq('category_id', filters.categoryId);
  if (filters.status) query = query.eq('status', filters.status);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as ReceiptListItem[];
}

/** Every receipt linked to a minister (their history), newest first. */
export async function getReceiptsByMinister(ministerId: string): Promise<ReceiptListItem[]> {
  const { data, error } = await db
    .from('receipts')
    .select(RECEIPT_LIST_SELECT)
    .eq('minister_id', ministerId)
    .order('issued_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ReceiptListItem[];
}

export async function getReceiptById(id: string): Promise<Receipt> {
  const { data, error } = await db
    .from('receipts')
    .select(RECEIPT_SELECT)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data as Receipt;
}

/** Full receipts (lines + payments) for many ids, e.g. a bulk print. Order is not guaranteed. */
export async function getReceiptsByIds(ids: string[]): Promise<Receipt[]> {
  const out: Receipt[] = [];
  for (let i = 0; i < ids.length; i += 100) {
    const { data, error } = await db
      .from('receipts')
      .select(RECEIPT_SELECT)
      .in('id', ids.slice(i, i + 100));
    if (error) throw error;
    out.push(...((data ?? []) as Receipt[]));
  }
  return out;
}

/**
 * Finds the receipt for a source record, e.g. a tithe. An issued receipt wins;
 * otherwise the most recent void one, so a voided receipt can be replaced
 * without `.maybeSingle()` throwing once a source has more than one row.
 */
export async function getReceiptBySource(
  sourceType: string,
  sourceId: string,
): Promise<Receipt | null> {
  const { data, error } = await db
    .from('receipts')
    .select(RECEIPT_SELECT)
    .eq('source_type', sourceType)
    .eq('source_id', sourceId)
    .order('issued_at', { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as Receipt[];
  return rows.find((r) => r.status === 'issued') ?? rows[0] ?? null;
}

/**
 * Issues a receipt: assigns the next number and creates the receipt, its
 * lines, and its payments in one server-side transaction (issue_receipt RPC).
 * There is no client-side "create then update" sequence here on purpose — see
 * the migration for why a two-step flow is the thing that caused the tithes
 * duplicate-number bug.
 */
export async function issueReceipt(
  input: IssueReceiptInput,
  totalInWords: string,
): Promise<{ id: string; number: number; year: number }> {
  const lines = input.lines.map((l) => ({
    concept_id: l.concept_id || null,
    description: l.description,
    quantity: l.quantity,
    unit_price: l.unit_price,
  }));

  const payments = input.payments.map((p) => ({
    method: p.method,
    amount: p.amount,
    bank_account_id: p.bank_account_id || null,
    check_number: p.check_number || null,
    voucher_photo_url: p.voucher_photo_url || null,
  }));

  const { data, error } = await db.rpc('issue_receipt', {
    p_category_id: input.category_id,
    p_payer_name: input.payer_name,
    p_payer_ci: input.payer_ci || null,
    p_currency: input.currency,
    p_exchange_rate: input.currency === 'USD' ? parseFloat(input.exchange_rate) : null,
    p_discount: input.discount ? parseFloat(input.discount) : 0,
    p_total_in_words: totalInWords,
    p_source_type: input.source_type ?? null,
    p_source_id: input.source_id ?? null,
    p_custom: input.custom ?? {},
    p_lines: lines,
    p_payments: payments,
    // Only sent when set, so issuing keeps working against a DB that predates the param.
    ...(input.minister_id ? { p_minister_id: input.minister_id } : {}),
  });
  if (error) throw error;
  // The RPC returns out_* column names: its OUT params are deliberately prefixed
  // so they cannot collide with table column names inside the function body
  // (see migration 20260910000004). Mapped back here so callers keep a clean shape.
  const row = (Array.isArray(data) ? data[0] : data) as {
    out_id: string; out_number: number; out_year: number;
  };
  return {
    id: row.out_id,
    number: row.out_number,
    year: row.out_year,
  };
}

/**
 * Source ids that already have an issued receipt (e.g. every tithe with one).
 * Paged with a stable order so >1000 rows don't get truncated by PostgREST;
 * normally a single request, cached by the caller.
 */
export async function getIssuedReceiptSourceIds(sourceType: string): Promise<Set<string>> {
  const ids = new Set<string>();
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await db
      .from('receipts')
      .select('source_id')
      .eq('source_type', sourceType)
      .eq('status', 'issued')
      .order('id')
      .range(from, from + PAGE - 1);
    if (error) throw error;
    for (const r of data ?? []) ids.add(r.source_id as string);
    if ((data?.length ?? 0) < PAGE) break;
  }
  return ids;
}

export async function voidReceipt(id: string, reason: string): Promise<void> {
  const { error } = await db.rpc('void_receipt', { p_receipt_id: id, p_reason: reason });
  if (error) throw error;
}

/** Public, token-gated lookup — no auth required. Used by the /r/:token page. */
export async function getPublicReceipt(token: string): Promise<PublicReceipt> {
  const { data, error } = await db.rpc('get_public_receipt', { p_token: token });
  if (error) throw error;
  return data as PublicReceipt;
}
